import { INVOICE_ISSUE_METHOD } from "../constants/Invoice";
import { ISSUE_DATE_MODES } from "../constants/Dates";
import { getDiffDate, isFutureDate, isToday } from "../utils/dates";
import { isObjectEmpty } from "../utils/object";
import {isValidPhoneNumber} from '../utils/phone';
import {isValidUrl} from '../utils/url';

export const validateOptionalPostalCode = (postal_code) => {
    if (!postal_code) return undefined;
    return validatePostalCode(postal_code);
}

export const validatePostalCode = (postal_code) => {
    postal_code = postal_code.replace(/\D/g,'');
    if (postal_code && (postal_code.length === 5 || postal_code.length === 9 )) return undefined;
    return 'Please enter a valid zip code';
}

export const validateEmail = (email) => {
    const emailReg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (email && !emailReg.test(String(email).toLowerCase())) {
        return 'Please enter a valid email address';
    }
    return undefined;
}

export const validateEmails = (emails) => {
    if (emails) {
        const emailsArray = emails.split(',');
        const result = emailsArray.map((i) => validateEmail(i.trim())).filter(Boolean)
        return result.length > 0 ? result.join(',') : undefined;
    } else {
        return undefined;
    }
}

export const validateOptionalPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return undefined;
    return validatePhoneNumber(phoneNumber);
}

export const validatePhoneNumber = (phoneNumber) => {
    const isValidPhone = isValidPhoneNumber(phoneNumber);
    if (!phoneNumber || !isValidPhone) {
        return 'Please enter a valid phone number';
    }
    return undefined;
}

export const validateUrl = (url) => {
    const isValid = isValidUrl(url);
    if (!url || !isValid) {
        return 'Please enter a valid url';
    }
    return undefined;
}

export const isOptionalValidateUrl = (url) => {
    if(!url) return undefined;
    return validateUrl(url);
}

export const isPositiveNumber = (value) => {
    if (isNaN(value)) return 'Numbers only';
    if (Number(value) <= 0) return "Positive numbers only";
};

export const isPositiveNumberOrZero = (value) => {
    if (isNaN(value)) return 'Numbers only';
    if (Number(value) < 0) return "Positive numbers or zero only";
};

export const isRequired = (value) => !value ? 'required' : undefined;

export const isRequiredPositiveNumber = (value) => {
    if (!value) return 'required';
    return isPositiveNumber(value);
};

export const isOptionalPositiveNumber = (value) => {
    if (!value) return undefined;
    return isPositiveNumber(value);
};

export const isOptionalPositiveNumberOrZero = (value) => {
    if (!value) return undefined;
    return isPositiveNumberOrZero(value);
};

export const isRequiredPositiveNumberOrZero = (value) => {
    if (value === undefined || value === null || value === '') return 'required';
    return isPositiveNumberOrZero(value);
};

const EDIT_INVOICE_FORM_VALIDATION = {
}

const EDIT_ORDER_FORM_VALIDATION = {
    'issued_at': (date, formValues) => {
        // Only validate if issue_date_mode is explicitly set to 'scheduled'
        if (formValues?.issue_date_mode !== ISSUE_DATE_MODES.SCHEDULED) return undefined;
        if (!date) return 'required';
        return undefined;
    },
    'due_date': (date, formValues) => {
        if (formValues?.payment_terms !== "fixed") return undefined; // not concerned about due date if the payment terms is not fixed
        if (!date) return 'required';
        const today = new Date();
        if (date && (getDiffDate(today, new Date(date), false) < 0)) return 'Please enter a day in the future.';
        return undefined;
    },
    'recipient_id': (recipient) => {
        if (!recipient) return 'You must search for a customer and select an option.';
    },
    'discount_amount': isOptionalPositiveNumberOrZero,
    'discount_percentage': (percentage) => {
        const err = isOptionalPositiveNumberOrZero(percentage);
        if (err) return err;
        if (percentage > 100) return ("Discount percentage should be less than 100");
    },
    'shipping_amount': isOptionalPositiveNumberOrZero,
    'tax_percentage': isOptionalPositiveNumberOrZero,
    'line_items': {
        'description': isRequired,
        'quantity': isRequiredPositiveNumberOrZero,
        'net_amount': isPositiveNumberOrZero,
    },
}

const DRAFT_SERIES_VALIDATION = {
    'frequency': isRequired,
    'start_date': (date) => {
        if (!date) return 'required';
        if (!isFutureDate(date) && !isToday(date)) return 'Please enter a day in the future.';
    },
    'period': isRequired
};

const MILESTONE_VALIDATION = {
    'name': isRequired,
    'amount': isRequired,
    'due_date': (date) => {
        if (!date) return; // due date can be undefined or null
    },
    'net_terms': (netTerms) => { 
        if (netTerms === 0) return;
        return isRequired(netTerms)
    }
};

const validateLineItems = (items) => {
    let itemErrors = {};
    // validate each individual items
    (Object.keys(items)).forEach((lineId) => {
        const item = items[lineId];
        const itemErr = {};
        // validate each field in the item
        (Object.keys(item)).forEach((name) => {
            let validator = EDIT_ORDER_FORM_VALIDATION['line_items'][name];
            if (validator) {
                const errMsg = validator(item[name]);
                if (errMsg) {
                    itemErr[name] = errMsg;
                }
            }
        });
        if (!isObjectEmpty(itemErr)) {
            itemErrors[lineId] = itemErr;
        }
    });
    return itemErrors;
}

const validateDraftSeries = (values) => {
    let err = {};
    (Object.keys(values)).forEach((name) => {
        let validator = DRAFT_SERIES_VALIDATION[name];
        if (validator) {
            const errMsg = validator(values[name], values);
            if (errMsg) {
                err[name] = errMsg;
            }
        }
    });
    return err;
}

const validateMilestones = (milestones) => {
    let milestoneErrs = {};
    let lastDueDate;
    // validate each individual items
    (Object.keys(milestones)).forEach((milestoneId) => {
        const milestone = milestones[milestoneId];
        const milestoneErr = {};
        // validate each field in the item
        (Object.keys(milestone)).forEach((name) => {
            let validator = MILESTONE_VALIDATION[name];
            if (validator) {
                const errMsg = validator(milestone[name]);
                if (errMsg) {
                    milestoneErr[name] = errMsg;
                }
            }
        });
        if (lastDueDate && milestone?.due_date && getDiffDate(lastDueDate, new Date(milestone?.due_date)) < 0) {
            milestoneErr['due_date'] = 'Due date cannot be earlier than previous milestone(s)';
        }
        // if merchant set fixed net term for due date, they have to specify a date
        if (milestone?.net_terms === "fixed" && !milestone?.due_date) {
            milestoneErr['due_date'] = 'required';
        }

        if (milestone?.issue_method === INVOICE_ISSUE_METHOD.SPECIFIED && !milestone?.issued_at) {
            milestoneErr['issued_at'] = 'required';
        }
        if (!isObjectEmpty(milestoneErr)) {
            milestoneErrs[milestoneId] = milestoneErr;
        }
        lastDueDate = milestone?.due_date ? new Date(milestone?.due_date) : '';
    });
    return milestoneErrs;
}

export const validateEditOrderForm = (fullFormValues) => {
    const {
        items, 
        is_series: isSeries, 
        draft_series: draftSeries, 
        has_milestones: hasMilestones,
        milestones,
        ...values
    } = fullFormValues;

    let err = {};
    // validate all formfields but items
    (Object.keys(values)).forEach((name) => {
        let validator = EDIT_ORDER_FORM_VALIDATION[name];
        if (validator) {
            const errMsg = validator(values[name], values);
            if (errMsg) {
                err[name] = errMsg;
            }
        }
    });

    const isShopifyOrder = !!values.shopify_order_id || values?.integration?.type === 'shopify';
    const itemErrors = !isShopifyOrder && validateLineItems(items); // Skip line item validation for shopify orders
    const draftSeriesErrors = isSeries ? validateDraftSeries(draftSeries) : undefined;
    const milestoneErrors = hasMilestones && !isSeries ? validateMilestones(milestones) : undefined;

    // TODO: validate that all milestones should add up to total
    // TODO: milestone issue date should be chronological
    err = {
        ...err,
        ...(isObjectEmpty(itemErrors) ? {} : {items: itemErrors}),
        ...(isObjectEmpty(draftSeriesErrors) ? {} : {draft_series: draftSeriesErrors}),
        ...(isObjectEmpty(milestoneErrors) ? {} : {milestones: milestoneErrors})
    };
    return isObjectEmpty(err) ? undefined : err;
}

export const validateEditInvoiceForm = (formValues) => {
    const {
        has_milestones: hasMilestones,
        milestones,
        ...values
    } = formValues;

    let err = {};
    // validate all formfields but items
    (Object.keys(values)).forEach((name) => {
        let validator = EDIT_INVOICE_FORM_VALIDATION[name];
        if (validator) {
            const errMsg = validator(values[name], values);
            if (errMsg) {
                err[name] = errMsg;
            }
        }
    });

    const milestoneErrors = hasMilestones ? validateMilestones(milestones) : undefined;
    err = {
        ...err,
        ...(isObjectEmpty(milestoneErrors) ? {} : {milestones: milestoneErrors})
    };

    return isObjectEmpty(err) ? undefined : err;
}

export const isUndefined = (val) => typeof val === "undefined";
// Merge tags are Ruby format-string style: %{customer_name}. The backend renders
// templates with sprintf, which raises KeyError on a tag it wasn't passed and
// ArgumentError on an unclosed one. On the async send path that raise is
// swallowed, so a typo'd tag means the email silently never goes out. A bare %
// is safe: Template#format_subject/format_message escape it to %% before saving,
// which is also why nothing here should escape anything.
//
// Angle brackets are deliberately allowed. The body is plain text that sprintf
// renders and SendGrid escapes, so they raise nothing, and rejecting them would
// fail bodies that are already saved and unrelated to whatever is being edited.
const MERGE_TAG_PATTERN = /%\{([^{}]*)\}/g;

export const validateTemplateText = (text, allowedTags) => {
    if (!text) return undefined;

    // Anything still holding a %{ once the well-formed tags are gone is unclosed.
    if (text.replace(MERGE_TAG_PATTERN, '').includes('%{')) {
        return 'A %{ is missing its closing }';
    }

    const tags = [...text.matchAll(MERGE_TAG_PATTERN)].map(([, name]) => name);
    if (tags.some(name => !name.trim())) return '%{} needs a variable name';

    // The backend sends the allowed set per template. Until it does, well-formed
    // tags pass: rejecting every one of them would be worse than missing a typo.
    if (!allowedTags) return undefined;

    const unknown = [...new Set(tags.filter(name => !allowedTags.includes(name)))];
    if (unknown.length) {
        const list = unknown.map(name => `%{${name}}`).join(', ');
        // This is a hard block, and the editor shows the allowed set nowhere else,
        // so the message has to carry it or the merchant has no way forward.
        const available = allowedTags.map(name => `%{${name}}`).join(', ');
        return `${list} ${unknown.length > 1 ? "aren't variables" : "isn't a variable"} you can use here. Available: ${available}`;
    }
    return undefined;
};
